import { io } from 'socket.io-client'
import type { Socket } from 'socket.io-client'
import type { Ack } from '@sh/shared'

let socket: Socket | null = null

/** One shared connection for the tab. Socket.IO handles the reconnect backoff. */
export function getSocket(): Socket {
  if (!socket) {
    socket = io({ transports: ['websocket', 'polling'], autoConnect: true })
  }
  return socket
}

/** Emit with an acknowledgement, surfacing server-side rejections as errors. */
export function request<T>(event: string, payload: unknown): Promise<Ack<T>> {
  return new Promise((resolve) => {
    const s = getSocket()
    const timer = setTimeout(
      () => resolve({ ok: false, error: 'the server did not answer - check your connection' }),
      8000,
    )
    s.emit(event, payload, (ack: Ack<T>) => {
      clearTimeout(timer)
      resolve(ack ?? { ok: false, error: 'no response' })
    })
  })
}

/**
 * Force a connection attempt now, rather than waiting out the backoff.
 *
 * Socket.IO retries on its own and never gives up, but the gap between
 * attempts grows to several seconds - which is a long time to sit in front of
 * a screen that has just told you it cannot reach anything. The "try again"
 * on the connecting screen is this: not a new socket, not a reload, just the
 * next attempt brought forward. On an already-open socket it does nothing.
 */
export function reconnect(): void {
  const s = getSocket()
  if (!s.connected) s.connect()
}
